import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import DomainGuideStatusDropdown from './DomainGuideStatusDropdown';

const DomainGuideDetails = (props) => {
  const {
    createdByLabel,
    domainGuideData,
    updateStatus,
  } = props;

  const [status, setStatus] = React.useState(domainGuideData.status);
  const emptyValue = translate('domainGuide.Detail.EmptyValue');
  const createdAtDate = new Date(domainGuideData.createdAt);
  const createdAtLabel = Number.isNaN(createdAtDate.getTime()) ?
    emptyValue :
    createdAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const updatedAtDate = new Date(domainGuideData.updatedAt);
  const updatedAtLabel = Number.isNaN(updatedAtDate.getTime()) ?
    emptyValue :
    updatedAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const detailSections = [
    {
      key: 'domainGuideRn',
      label: translate('domainGuide.Detail.Fields.DomainGuideId'),
      value: domainGuideData.domainGuideRn || emptyValue,
    },
    {
      key: 'description',
      label: translate('domainGuide.Detail.Fields.Description'),
      value: domainGuideData.description || emptyValue,
    },
    {
      key: 'status',
      label: translate('domainGuide.Detail.Fields.Status'),
      type: 'statusDropdown',
    },
    {
      key: 'createdBy',
      label: translate('domainGuide.Detail.Fields.CreatedBy'),
      value: createdByLabel || domainGuideData.createdBy || emptyValue,
    },
    {
      key: 'createdAt',
      label: translate('domainGuide.Detail.Fields.CreatedAt'),
      value: createdAtLabel,
    },
    {
      key: 'updatedAt',
      label: translate('domainGuide.Detail.Fields.UpdatedAt'),
      value: updatedAtLabel,
    },
  ];

  React.useEffect(() => {
    setStatus(domainGuideData.status);
  }, [domainGuideData.status]);

  const handleSetStatus = (selectedStatus) => {
    if (!selectedStatus || selectedStatus === status) {
      return;
    }

    setStatus(selectedStatus);
    updateStatus(selectedStatus);
  };

  return (
    <Stack gap={1}>
      <Typography sx={{fontSize: '28px', fontWeight: 400, wordBreak: 'break-word'}}>
        {domainGuideData.name || emptyValue}
      </Typography>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#E8E4DE',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: 'repeat(2, minmax(0, auto))',
              md: 'repeat(3, minmax(0, auto))',
            },
          }}
        >
          {
            detailSections.map((section) => (
              <Box
                key={section.key}
                sx={{
                  minWidth: 0,
                  backgroundColor: '#FFFFFF',
                  px: 3,
                  py: 2,
                }}
              >
                {
                  section.type === 'statusDropdown' &&
                  <DomainGuideStatusDropdown
                    label={section.label}
                    selectedStatus={status}
                    setSelectedStatus={handleSetStatus}
                  />
                }
                {
                  !section.type &&
                  <Stack
                    sx={{
                      gap: '6px',
                      minWidth: 0,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '11px',
                        lineHeight: '14px',
                        color: '#A8A49E',
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#000000',
                        wordBreak: 'break-word',
                      }}
                    >
                      {section.value}
                    </Typography>
                  </Stack>
                }
              </Box>
            ))
          }
        </Box>
      </Box>
    </Stack>
  );
};

DomainGuideDetails.propTypes = {
  createdByLabel: PropTypes.string,
  domainGuideData: PropTypes.object.isRequired,
  updateStatus: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideDetails);
